var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { LitElement, html, css } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { localized, msg } from '@lit/localize';
import { DataFrame } from '../../../model/DataFrame.js';
import { getDataFrameFromPois, poisCreateFromDataFrame, } from '../../../utils/poi.js';
import { CompareTable } from '../../table/CompareTable.js';
import { CommonActions } from '../../../localization/helpers.js';
let NetworkUpdatePage = class NetworkUpdatePage extends LitElement {
    constructor() {
        super(...arguments);
        this.currentStep = 1;
        this.steps = [];
        this.pois = [];
        this.errorMessage = null;
        this.dataFrame = null;
        this.importFile = null;
        this.newNetworkFields = [];
        this.newDataFrame = null;
        this.mergeMethod = 'add';
    }
    connectedCallback() {
        super.connectedCallback();
        this.steps = [
            msg('Import File', { id: 'network.update.step1', desc: 'Network update step 1' }),
            msg('Configuration', { id: 'network.update.step2', desc: 'Network update step 2' }),
            msg('Update Options', { id: 'network.update.step3', desc: 'Network update step 3' }),
        ];
    }
    renderStep1() {
        return html `
      <p>
        ${msg('Import a file for the network', { id: 'network.import.instruction', desc: 'Import file instruction' })}
        <strong>${this.network.name}</strong>. ${msg('Supported formats: CSV and Excel.', { id: 'network.import.supportedFormats', desc: 'Supported file formats' })}
      </p>
      <label for="network-file">${msg('Network File', { id: 'network.label.file', desc: 'Network file label' })}</label>
      <input-file
        id="network-file"
        label=${msg('Network File', { id: 'network.label.file', desc: 'Network file label' })}
        .fileTypes=${['.csv', '.xlsx']}
        @file-selected=${(e) => {
            this.importFile = e.detail[0];
        }}
      ></input-file>
    `;
    }
    renderStep2() {
        return html `
      <p>
        ${msg('Configure the network settings for', { id: 'network.config.instruction', desc: 'Configuration instruction' })}
        <strong>${this.network.name}</strong>.
      </p>
      <div class="col-container">
        <file-table
          class="col"
          .file=${this.importFile}
          .rowsPerPage=${20}
          @dataframe-loaded=${(e) => {
            this.dataFrame = e.detail;
        }}
        ></file-table>
        <network-field-manager
          class="col"
          .dataFrame=${this.renderRoot.querySelector('file-table')?.getDataFrame()}
          .networkFields=${this.networkFields}
        ></network-field-manager>
      </div>
    `;
    }
    renderStep3() {
        const df_old = getDataFrameFromPois(this.pois, this.networkFields);
        const df_new = this.newDataFrame;
        df_old.index_column = 'id';
        df_new.index_column = 'id';
        df_old.deleteColumn('_status');
        df_new.deleteColumn('_status');
        df_old.deleteColumn('poi_id');
        df_new.deleteColumn('poi_id');
        return html `
      <p>
        ${msg('Summary of network update for', { id: 'network.update.summaryInstruction', desc: 'Update summary instruction' })}
        <strong>${this.network.name}</strong> ${msg('and choice of update type.', { id: 'network.update.summaryChoice', desc: 'Update choice instruction' })}
      </p>
      <compare-table
        .oldDataFrame=${df_old}
        .newDataFrame=${df_new}
        .vocabulary=${{
            dataframe: msg('Network', { id: 'network.vocabulary.dataframe', desc: 'Network (singular)' }),
            dataframes: msg('Networks', { id: 'network.vocabulary.dataframes', desc: 'Networks (plural)' }),
            rows: msg('points', { id: 'network.vocabulary.rows', desc: 'Points (plural)' }),
            row: msg('point', { id: 'network.vocabulary.row', desc: 'Point (singular)' }),
            columns: msg('attributes', { id: 'network.vocabulary.columns', desc: 'Attributes (plural)' }),
            column: msg('attribute', { id: 'network.vocabulary.column', desc: 'Attribute (singular)' }),
        }}
        @merge-method-change=${(e) => {
            this.mergeMethod = e.detail;
        }}
      ></compare-table>
    `;
    }
    getPoiToAdd() {
        const compareDataFrames = this.shadowRoot?.querySelector('compare-table');
        const comparisons = compareDataFrames?.getComparisons();
        if (!comparisons || !comparisons.addedRows) {
            return []; // Retourne un tableau vide si les données sont absentes
        }
        if (this.mergeMethod === 'add') {
            const networkField = this.networkFields;
            this.newNetworkFields
                .filter((nf) => nf.native)
                .forEach((nf) => {
                if (!networkField.find((of) => of.name === nf.name)) {
                    networkField.push(nf);
                }
            });
            return (poisCreateFromDataFrame(comparisons.addedRows, this.networkFields) || []);
        }
        else {
            return (poisCreateFromDataFrame(comparisons.addedRows, this.newNetworkFields) ||
                []);
        }
    }
    getDeletedPois() {
        const compareDataFrames = this.shadowRoot?.querySelector('compare-table');
        const comparisons = compareDataFrames.getComparisons();
        if (!comparisons || !comparisons.removedRows) {
            return []; // Retourne un tableau vide si les données sont absentes
        }
        const deletedPois = poisCreateFromDataFrame(comparisons.removedRows, this.newNetworkFields);
        deletedPois.forEach((poi) => {
            const poi_id = this.pois.find((p) => p.id === poi.id)?.poi_id;
            if (poi_id) {
                poi.poi_id = poi_id;
            }
            else {
                console.warn(`POI with id ${poi.id} not found in existing POIs.`);
            }
        });
        return deletedPois; // Retourne toujours un tableau, même vide
    }
    getUpdatedPois() {
        const compareDataFrames = this.shadowRoot?.querySelector('compare-table');
        const comparisons = compareDataFrames?.getComparisons();
        if (!comparisons || !comparisons.modifiedRows) {
            return []; // Retourne un tableau vide si les données sont absentes
        }
        const updatedPois = poisCreateFromDataFrame(comparisons.modifiedRows, this.newNetworkFields);
        updatedPois.forEach((poi) => {
            const poi_id = this.pois.find((p) => p.id === poi.id)?.poi_id;
            if (poi_id) {
                poi.poi_id = poi_id;
            }
            else {
                console.warn(`POI with id ${poi.id} not found in existing POIs.`);
            }
        });
        return updatedPois || []; // Retourne toujours un tableau, même vide
    }
    handleSubmit() {
        console.log('Submitting with method:', this.mergeMethod);
        const poisToAdd = this.getPoiToAdd();
        const deletedPois = this.getDeletedPois();
        const updatedPois = this.getUpdatedPois();
        console.log('POIs to add:', poisToAdd);
        console.log('Deleted POIs:', deletedPois);
        console.log('Updated POIs:', updatedPois);
        console.log('New Network Fields:', this.newNetworkFields);
        if (this.mergeMethod === 'add') {
            this.dispatchEvent(new CustomEvent('network-update-add-submit', {
                detail: {
                    network: this.network,
                    newNetworkFields: this.newNetworkFields,
                    poisToAdd: poisToAdd || [], // Assurez-vous que c'est un tableau
                },
                bubbles: true,
                composed: true,
            }));
        }
        else if (this.mergeMethod === 'replace') {
            this.dispatchEvent(new CustomEvent('network-update-replace-submit', {
                detail: {
                    network: this.network,
                    newNetworkFields: this.newNetworkFields,
                    poisToAdd: poisCreateFromDataFrame(this.newDataFrame, this.newNetworkFields) || [],
                },
                bubbles: true,
                composed: true,
            }));
        }
        else if (this.mergeMethod === 'update') {
            this.dispatchEvent(new CustomEvent('network-update-update-submit', {
                detail: {
                    network: this.network,
                    newNetworkFields: this.newNetworkFields,
                    deletedPois: deletedPois || [],
                    updatedPois: updatedPois || [],
                    newPois: poisToAdd || [],
                },
                bubbles: true,
                composed: true,
            }));
        }
    }
    handleNext() {
        if (this.currentStep === 1 && this.importFile === null) {
            this.errorMessage = msg('The network file is required', { id: 'error.network.fileRequired', desc: 'Network file required error' });
            return;
        }
        if (this.currentStep === 2) {
            // Récupérer les nouveaux champs
            const networkFieldManager = this.renderRoot.querySelector('network-field-manager');
            const { renameDict, networkFields } = networkFieldManager.getNetworkFields();
            // Vérifier qu'il y a un champ id
            if (!networkFields.find((nf) => nf.name === 'id' && nf.native)) {
                this.errorMessage = msg('A unique identifier is required to update the network', { id: 'error.network.idRequiredUpdate', desc: 'Unique ID required for update error' });
                return;
            }
            // Sauvegarder les nouveaux champs et le dictionnaire de renommage
            this.newNetworkFields = networkFields;
            // file df -> poi df
            this.dataFrame?.renameColumns(renameDict);
            this.dataFrame?.changeType('id', 'string');
            // Transform to poi and back to df
            const payload = poisCreateFromDataFrame(this.dataFrame, this.newNetworkFields);
            this.newDataFrame = getDataFrameFromPois(payload, this.newNetworkFields);
        }
        if (this.currentStep === 3) {
            this.handleSubmit();
            return;
        }
        if (this.currentStep < this.steps.length) {
            this.errorMessage = null;
            this.currentStep++;
        }
    }
    handlePrevious() {
        if (this.currentStep > 1) {
            this.currentStep--;
        }
    }
    render() {
        return html `
      <full-screen-modal>
        <div id="background-gradient"></div>
        <div id="inside-modal">
          <basic-card align="center" class="card">
            <h2>${msg('Update network', { id: 'network.update.title', desc: 'Update network title' })} ${this.network.name}</h2>
            <form-stepper
              .currentStep=${this.currentStep}
              .steps=${this.steps}
            ></form-stepper>
            <div class="step">
              ${this.currentStep === 1 ? this.renderStep1() : ''}
              ${this.currentStep === 2 ? this.renderStep2() : ''}
              ${this.currentStep === 3 ? this.renderStep3() : ''}
            </div>
            ${this.errorMessage
            ? html `<p style="color: red;">${this.errorMessage}</p>`
            : ''}
            <div class="buttons">
              ${this.currentStep > 1
            ? html `<simple-button
                    label=${CommonActions.previous()}
                    variant="secondary"
                    @button-click=${this.handlePrevious}
                  ></simple-button>`
            : ''}
              <simple-button
                special
                variant="primary"
                label=${CommonActions.next()}
                @button-click=${this.handleNext}
              ></simple-button>
            </div>
          </basic-card>
        </div>
      </full-screen-modal>
    `;
    }
};
NetworkUpdatePage.styles = css `
    :host {
      display: block;
      width: 100%;
      height: 100%;
    }

    #background-gradient {
      width: 100%;
      height: 100%;
      position: absolute;
      top: 0;
      left: 0;
      background: linear-gradient(135deg, #46c56cff, #4b7afcff);
      z-index: 1; /* Place the background below the content */
    }

    #inside-modal {
      position: absolute;
      z-index: 2; /* Place the content above the background */
      display: flex;
      justify-content: center;
      align-items: center;
      width: 100%;
      height: 100%;
    }

    .card {
      padding: 5px 20px;
      min-width: 50%;
      max-width: 50%;
      max-height: 100%;
    }

    @media (max-width: 1500px) {
    .card {
        min-width: 60%;
        max-width: 60%;
        }
    }

    @media (max-width: 1000px) {
    .card {
        min-width: 75%;
        max-width: 75%;
        }
    }

    label {
      display: block;
      text-align: left; /* Align the label to the left */
      margin-top: 15px;
      margin-bottom: 8px; /* Add some spacing below the label */
    }

    .buttons {
      margin: 10px 0;
      display: flex;
      justify-content: flex-end; /* Align buttons to the right */
      gap: 10px; /* Add spacing between buttons */
    }

    .col-container {
      display: flex;
      flex-wrap: wrap; /* Allow columns to wrap if needed */
      gap: 20px;
      max-width: 100%; /* Prevent the container from exceeding the parent width */
      box-sizing: border-box;
      align-items: stretch; /* Ensure columns stretch to the same height */
      height: calc(100vh - 375px);
    }

    .col {
      flex: 1; /* Ensure columns take equal space */
      min-width: 300px; /* Set a minimum width to prevent columns from becoming too small */
      max-width: 100%; /* Prevent columns from exceeding the container width */
      box-sizing: border-box; /* Include padding and border in width calculation */
      max-height: 100%;
      overflow: auto;
      width: 1500px;
    }

    file-table {
      width: 100%; /* Adjust to fit the container */
    }

    compare-table {
      width: 100%; /* Adjust to fit the container */
      overflow: auto;
      max-height: calc(100vh - 290px);
    }
  `;
__decorate([
    property({ type: Number })
], NetworkUpdatePage.prototype, "currentStep", void 0);
__decorate([
    property({ type: Array })
], NetworkUpdatePage.prototype, "steps", void 0);
__decorate([
    property({ type: Object })
], NetworkUpdatePage.prototype, "network", void 0);
__decorate([
    property({ type: Object })
], NetworkUpdatePage.prototype, "networkFields", void 0);
__decorate([
    property({ type: Object })
], NetworkUpdatePage.prototype, "pois", void 0);
__decorate([
    state()
], NetworkUpdatePage.prototype, "errorMessage", void 0);
__decorate([
    state()
], NetworkUpdatePage.prototype, "dataFrame", void 0);
NetworkUpdatePage = __decorate([
    localized(),
    customElement('network-update-page')
], NetworkUpdatePage);
export { NetworkUpdatePage };
//# sourceMappingURL=NetworkUpdatePage.js.map